import { SafeGround, SeatLayoutSectionRow, SeatLayoutSectionSeat } from "@playarena/shared";
import type React from "react";
import { useState } from "react";

import { cn } from "@/lib/utils";

interface SeatMapProps {
  ground: SafeGround;
  selectedSeats?: string[];
  unavailableSeats?: string[];
  onSeatClick?: (seatId: string) => void;
}

const SeatMap: React.FC<SeatMapProps> = ({
  ground,
  selectedSeats = [],
  unavailableSeats = [],
  onSeatClick,
}) => {
  const [hoveredSeat, setHoveredSeat] = useState<string | null>(null);

  const getSeatStatus = (seat: SeatLayoutSectionSeat) => {
    if (unavailableSeats.includes(seat.id)) return "unavailable";
    if (selectedSeats.includes(seat.id)) return "selected";
    return "available";
  };

  const getSeatClassName = (seat: SeatLayoutSectionSeat) => {
    const status = getSeatStatus(seat);
    const baseClasses =
      "seat w-6 h-6 rounded text-xs flex items-center justify-center cursor-pointer transition-all";

    switch (status) {
      case "selected":
        return `${baseClasses} seat-selected`;
      case "unavailable":
        return `${baseClasses} seat-booked`;
      case "available":
      default:
        return `${baseClasses} seat-available`;
    }
  };

  const getRowClassName = (row: SeatLayoutSectionRow) => {
    const baseClasses =
      "row w-6 h-6 rounded text-xs flex items-center justify-center cursor-pointer transition-all";

    return baseClasses;
  };

  const handleSeatClick = (seat: SeatLayoutSectionSeat) => {
    if (getSeatStatus(seat) === "unavailable") return;
    onSeatClick?.(seat.id);
  };

  return (
    <div className='mx-auto w-full max-w-4xl'>
      {/* Stage/Field */}
      <div className='mb-8'>
        <div className='flex h-16 w-full items-center justify-center rounded-lg bg-gradient-to-r from-green-400 to-green-600 font-semibold text-white'>
          FIELD / STAGE
        </div>
      </div>

      {/* Seating Sections */}
      <div className='space-y-8'>
        {ground.seatLayout &&
          ground.seatLayout.sections.map(section => (
            <div key={section.id} className='rounded-lg border p-4'>
              <div className='mb-4 flex items-center justify-between'>
                <h3 className='text-lg font-semibold'>{section.name}</h3>
                <div className='text-sm capitalize text-muted-foreground'>
                  {section.type} - ${(ground.pricing.basePrice * section.price).toFixed(2)}
                </div>
              </div>

              {/* Seats Grid */}
              <div
                className='grid gap-1'
                style={{
                  gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(section.rows?.length || 4))}, minmax(0, 1fr))`,
                }}
              >
                {section.rows &&
                  section.rows.map((row, index) => (
                    <div
                      key={row.id}
                      className={cn("grid gap-1", getRowClassName(row))}
                      style={{
                        gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(row.seats?.length || 4))}, minmax(0, 1fr))`,
                      }}
                      title={`${section.name} - Row ${row.name}, Row ${index}`}
                    >
                      {row.seats &&
                        row.seats?.map(seat => (
                          <div
                            key={seat.id}
                            className={getSeatClassName(seat)}
                            onClick={() => handleSeatClick(seat)}
                            onMouseEnter={() => setHoveredSeat(seat.id)}
                            onMouseLeave={() => setHoveredSeat(null)}
                            title={`${section.name} - Row ${row.name}, Seat ${seat.number}`}
                          >
                            {seat.number}
                          </div>
                        ))}
                    </div>
                  ))}
              </div>
            </div>
          ))}
      </div>

      {/* Seat Info Tooltip */}
      {hoveredSeat && (
        <div className='fixed bottom-4 left-1/2 z-50 -translate-x-1/2 transform rounded bg-black px-3 py-2 text-sm text-white'>
          Seat {hoveredSeat}
        </div>
      )}
    </div>
  );
};

export default SeatMap;
